/**
 * Diagnostics normalisation + indexing. The production compiler is the linter (spec §7.1): the
 * `POST /v1/flows/{id}/validate` result is authoritative and carries element refs (nodeId/edgeId).
 * The client-side `previewDiagnostics` (parse.ts) is a faithful subset used before the first server
 * round-trip. Both fold into one `UiDiagnostic` shape the canvas + problems panel render.
 */
import type { ServerDiagnostic, ValidationResult } from '@/api/types';
import type { Diagnostic } from './parse';

export type Severity = 'error' | 'warning' | 'info';

export interface UiDiagnostic {
  severity: Severity;
  code: string;
  message: string;
  nodeId?: string;
  edgeId?: string;
  docAnchor?: string;
  /** 'server' = the real compiler; 'preview' = the client subset. */
  source: 'server' | 'preview';
}

const SEV_RANK: Record<Severity, number> = { error: 3, warning: 2, info: 1 };

export function severityRank(s: Severity): number {
  return SEV_RANK[s];
}

function normSeverity(s: ServerDiagnostic['severity']): Severity {
  switch (s) {
    case 'ERROR':
      return 'error';
    case 'WARNING':
      return 'warning';
    default:
      return 'info';
  }
}

export function fromServerDiagnostic(d: ServerDiagnostic): UiDiagnostic {
  return {
    severity: normSeverity(d.severity),
    code: d.code,
    message: d.message,
    nodeId: d.nodeId ?? undefined,
    edgeId: d.edgeId ?? undefined,
    docAnchor: d.docAnchor,
    source: 'server',
  };
}

/** Fold a ValidationResult (errors + warnings arrays) into a single ordered UiDiagnostic list. */
export function fromValidation(v: ValidationResult): UiDiagnostic[] {
  return [...v.errors, ...v.warnings].map(fromServerDiagnostic);
}

export function fromPreview(d: Diagnostic[]): UiDiagnostic[] {
  return d.map((x) => ({ ...x, source: 'preview' as const }));
}

export interface DiagnosticIndex {
  all: UiDiagnostic[];
  byNode: Map<string, UiDiagnostic[]>;
  byEdge: Map<string, UiDiagnostic[]>;
  /** Worst severity present on a given node/edge id (for badge colour). */
  worstNode: Map<string, Severity>;
  worstEdge: Map<string, Severity>;
  flowLevel: UiDiagnostic[];
  errors: number;
  warnings: number;
  infos: number;
}

function worse(a: Severity | undefined, b: Severity): Severity {
  return a && SEV_RANK[a] >= SEV_RANK[b] ? a : b;
}

export function indexDiagnostics(all: UiDiagnostic[]): DiagnosticIndex {
  const byNode = new Map<string, UiDiagnostic[]>();
  const byEdge = new Map<string, UiDiagnostic[]>();
  const worstNode = new Map<string, Severity>();
  const worstEdge = new Map<string, Severity>();
  const flowLevel: UiDiagnostic[] = [];
  let errors = 0;
  let warnings = 0;
  let infos = 0;

  for (const d of all) {
    if (d.severity === 'error') errors += 1;
    else if (d.severity === 'warning') warnings += 1;
    else infos += 1;

    if (d.nodeId) {
      const arr = byNode.get(d.nodeId) ?? [];
      arr.push(d);
      byNode.set(d.nodeId, arr);
      worstNode.set(d.nodeId, worse(worstNode.get(d.nodeId), d.severity));
    } else if (d.edgeId) {
      const arr = byEdge.get(d.edgeId) ?? [];
      arr.push(d);
      byEdge.set(d.edgeId, arr);
      worstEdge.set(d.edgeId, worse(worstEdge.get(d.edgeId), d.severity));
    } else {
      flowLevel.push(d);
    }
  }

  return { all, byNode, byEdge, worstNode, worstEdge, flowLevel, errors, warnings, infos };
}

export const EMPTY_DIAGNOSTIC_INDEX: DiagnosticIndex = indexDiagnostics([]);
